/** Client-side header sniffing only - the file's actual rows are parsed server-side
 * (`ImportCsvParser`, taler-backend). This reads just enough of the file to populate the
 * mapping selects and to guess a first pick; a wrong guess is corrected by picking a
 * different header from the same select, never by restarting. */

/** A single CSV record, comma-delimited RFC 4180, quotes escaped by doubling - mirrors the
 * server's own parser closely enough for a header row (no embedded newlines expected there). */
function splitRecord(line: string): string[] {
  const fields: string[] = []
  let field = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i += 1) {
    const c = line[i]
    if (inQuotes) {
      if (c === '"' && line[i + 1] === '"') {
        field += '"'
        i += 1
      } else if (c === '"') {
        inQuotes = false
      } else {
        field += c
      }
    } else if (c === '"') {
      inQuotes = true
    } else if (c === ',') {
      fields.push(field)
      field = ''
    } else {
      field += c
    }
  }
  fields.push(field)
  return fields
}

export async function sniffHeaders(file: File): Promise<readonly string[]> {
  const text = await file.text()
  const withoutBom = text.startsWith('﻿') ? text.slice(1) : text
  const firstLine = withoutBom.split(/\r\n|\r|\n/, 1)[0] ?? ''
  if (firstLine.trim() === '') return []
  return splitRecord(firstLine).map((header) => header.trim())
}

export interface MappingGuess {
  readonly date: string | undefined
  readonly amount: string | undefined
  readonly description: string | undefined
  readonly accountCode: string | undefined
  readonly externalReference: string | undefined
}

const KEYWORDS: Readonly<Record<keyof MappingGuess, readonly string[]>> = {
  date: ['date', 'dátum'],
  amount: ['amount', 'összeg'],
  description: ['description', 'memo', 'megnevezés', 'leírás', 'payee', 'kedvezményezett'],
  accountCode: ['account', 'category', 'számla', 'kategória', 'kód'],
  externalReference: ['reference', 'external', 'azonosító', 'referencia'],
}

/** First header whose name contains the keyword, case-insensitively - the same "first match
 * wins" heuristic the reference implementation used, without any of its dead configuration
 * (no decimal/thousand-separator guess: this contract has none). */
function guessField(headers: readonly string[], keywords: readonly string[]): string | undefined {
  for (const keyword of keywords) {
    const match = headers.find((header) => header.toLowerCase().includes(keyword))
    if (match !== undefined) return match
  }
  return undefined
}

export function guessMapping(headers: readonly string[]): MappingGuess {
  return {
    date: guessField(headers, KEYWORDS.date),
    amount: guessField(headers, KEYWORDS.amount),
    description: guessField(headers, KEYWORDS.description),
    accountCode: guessField(headers, KEYWORDS.accountCode),
    externalReference: guessField(headers, KEYWORDS.externalReference),
  }
}
